/**
 * `useRowsDrawer()` — open the `oem-source` drawer on rows the caller already
 * holds. Service / Customers / Reports modules, fork-owned (zone C).
 *
 * The demo answers "where did this number come from" with `audit(key, filter)`
 * (line 1907): it looks the dataset up in `const SRC` (line 1875), applies an
 * equality filter to the in-memory bundle and renders the slice under the
 * source file's real path, format and column order.
 *
 * The Issues lane ported the nine datasets its citations needed as
 * `modules/issues/dataset-source.ts`, which **fetches** the domain file on
 * click. The six surfaces in this lane already have their payloads loaded —
 * the page cannot render without them — so refetching to open a drawer would
 * be a second network round trip for rows sitting in the component's own
 * `useMemo`. This module takes the rows instead and only carries the half of
 * `SRC` that makes the drawer credible: the title, the path inside
 * `meridian-data/`, the wire format and the column order, verbatim.
 *
 * It is a hook (it opens a drawer), so it is engine layer rather than a
 * transform. The pure half — {@link sliceSpec}, {@link buildSlice} — has no
 * React in it and is exported separately.
 *
 * Adding a dataset: copy its row out of the demo's `SRC` table. Do not invent a
 * title, path or column order.
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — the payload contract
 * @see src/versions/v1/modules/issues/dataset-source.ts — the fetching variant
 */

import { useCallback } from 'react';
import { useDrawer } from '@/app/providers';
import type { SourceCalc, SourceTablePayload } from '@/versions/v1/chrome-oem/SourceDrawerBody';
import { matchingIndexes, type RowFilter } from '@/versions/v1/shared/row-filter';

/** The datasets this lane's surfaces cite. Keys are the demo's `SRC` keys. */
export type SliceKey =
  | 'shift'
  | 'css'
  | 'roster'
  | 'lms'
  | 'techs'
  | 'timesheets'
  | 'xreach'
  | 'cases'
  | 'orders'
  | 'inv'
  | 'parts'
  | 'fleet'
  | 'health'
  | 'wos'
  | 'rmsf';

/** One dataset's provenance — the demo's `SRC[key]`, without its row getter. */
export interface SliceSpec {
  /** Drawer title. */
  readonly title: string;
  /** Path inside `meridian-data/`. */
  readonly path: string;
  /** Wire format tag, printed uppercase beside the title in the demo. */
  readonly fmt: string;
  /** Column order, verbatim. */
  readonly cols: readonly string[];
}

/** The demo's `SRC` table (line 1875), narrowed to this lane's citations. */
const SLICES: Readonly<Record<SliceKey, SliceSpec>> = {
  shift: {
    title: 'Shift records (daily rollup)',
    path: '01_telematics/shift_end/shift_records_all_mines.csv',
    fmt: 'csv',
    cols: [
      'machine_id',
      'date',
      'hours',
      'alarms',
      'abuse',
      'diesel_l',
      'kwh',
      'co2_kg',
      'delays',
    ],
  },
  css: {
    title: 'Customer satisfaction responses',
    path: '22_css/css_responses_2026-09.xlsx',
    fmt: 'xlsx',
    cols: [
      'Job',
      'WO',
      'Site',
      'Customer contact',
      'Technician',
      'Machine',
      'Date',
      'Quality (1-5)',
      'Responsiveness (1-5)',
      'Reporting (1-5)',
      'Overall',
      'Comment',
      'Channel',
    ],
  },
  roster: {
    title: 'Technician roster W37',
    path: '21_roster/technician_roster_2026-W37.xlsx',
    fmt: 'xlsx',
    cols: [
      'Technician',
      'Role',
      'Base site',
      'Roster',
      'Swing start',
      'Swing end',
      'Available W37',
      'Skills / certs',
      'Customer preference',
      'Vehicle',
    ],
  },
  lms: {
    title: 'Training records (LMS)',
    path: '20_lms/training_records_2026-09-09.csv',
    fmt: 'csv',
    cols: ['person', 'org', 'site', 'role', 'course', 'completed', 'expires', 'status', 'provider'],
  },
  techs: {
    title: 'Technician status (Maximo Mobile)',
    path: '23_technician_app/techapp_tech_status_2026-09-09.json',
    fmt: 'json',
    cols: [
      'technician',
      'base',
      'status',
      'current_job',
      'last_seen',
      'vehicle',
      'lat',
      'lon',
      'skills',
    ],
  },
  timesheets: {
    title: 'Technician timesheets W36 (Maximo Mobile)',
    path: '23_technician_app/techapp_timesheets_2026-W36.csv',
    fmt: 'csv',
    cols: [
      'technician',
      'date',
      'site',
      'job_hours',
      'pm_support_hours',
      'travel_hours',
      'standby_hours',
      'day_off',
      'jobs',
    ],
  },
  xreach: {
    title: 'Technician app jobs (Maximo Mobile)',
    path: '23_technician_app/techapp_jobs_2026-07-28_to_2026-09-09.json',
    fmt: 'json',
    cols: [
      'job_id',
      'wo_ref',
      'external_ref',
      'technician',
      'machine_id',
      'site',
      'job_type',
      'status',
      'on_site',
      'off_site',
      'labour_h',
      'parts',
    ],
  },
  cases: {
    title: 'Service cases',
    path: '03_parts_salesforce/service_cases.csv',
    fmt: 'csv',
    cols: ['case_no', 'opened', 'mine', 'machine_id', 'type', 'status', 'owner', 'subject'],
  },
  orders: {
    title: 'Parts orders',
    path: '03_parts_salesforce/order_history.csv',
    fmt: 'csv',
    cols: ['order_no', 'order_date', 'mine', 'machine_id', 'part_no', 'qty', 'status', 'linked_wo'],
  },
  inv: {
    title: 'Inventory snapshot',
    path: '03_parts_salesforce/inventory_snapshot.csv',
    fmt: 'csv',
    cols: ['location', 'part_no', 'qty_on_hand', 'min_level'],
  },
  parts: {
    title: 'Parts catalogue',
    path: '03_parts_salesforce/parts_catalogue.csv',
    fmt: 'csv',
    cols: [
      'part_no',
      'description',
      'fits',
      'system',
      'list_price_aud',
      'lead_time_days',
      'criticality',
    ],
  },
  fleet: {
    title: 'Fleet register',
    path: '00_fleet/fleet_register.csv',
    fmt: 'csv',
    cols: [
      'machine_id',
      'mine',
      'model',
      'division',
      'subclass',
      'drive',
      'operating_mode',
      'serial',
      'commissioned',
      'engine_hours',
      'list_price_aud',
    ],
  },
  health: {
    title: 'Component condition register',
    path: '00_fleet/component_health.csv',
    fmt: 'csv',
    cols: ['machine_id', 'component', 'health_score', 'status', 'hours_since_change', 'note'],
  },
  wos: {
    title: 'Work orders',
    path: '02_sap_pm/work_orders.csv',
    fmt: 'csv',
    cols: [
      'wo_id',
      'mine',
      'machine_id',
      'wo_type',
      'status',
      'created',
      'component',
      'downtime_h',
      'labour_h',
      'total_direct_cost_aud',
      'downtime_cost_aud',
      'root_cause',
    ],
  },
  rmsf: {
    title: 'RMS fleet status',
    path: '15_rms/rms_fleet_status_2026-09-09.csv',
    fmt: 'csv',
    cols: [
      'machine_id',
      'model',
      'site',
      'status',
      'last_heard',
      'engine_h',
      'drilling_h',
      'pattern',
      'hole',
      'signal_dbm',
    ],
  },
};

/** The provenance record for a dataset. */
export const sliceSpec = (key: SliceKey): SliceSpec => SLICES[key];

/**
 * A dataset row.
 *
 * Deliberately `object` rather than `Record<string, unknown>`: callers pass the
 * schema's own interfaces (`WorkOrder`, `ServiceCase`, …), and a declared
 * interface has no index signature, so the stricter type would force every call
 * site to cast. The renderer reads columns by name and tolerates a miss, which
 * is exactly what the loose type admits.
 */
export type SliceRow = object;

/** One row, addressed by column name. */
const at = (row: SliceRow, key: string): unknown => (row as Record<string, unknown>)[key];

/** Options for one drawer opening. */
export interface SliceOptions {
  /** Overrides the dataset's own title on the drawer header. */
  readonly title?: string;
  /** Rows in the whole file, when the caller passes a filtered subset. */
  readonly total?: number;
  /** How the subset was chosen, e.g. `'technician=Dale Whitmore'`. */
  readonly filter?: string;
  /** Overrides the dataset's column order — used where a view adds a column. */
  readonly cols?: readonly string[];
  /** Rows to mark — the demo's `opts.hl`, as a declarative filter. */
  readonly highlight?: RowFilter;
  /** The "How this figure is calculated" note — the demo's `opts.calc`. */
  readonly calc?: SourceCalc;
}

/** Demo line 1907. A cell: one decimal on a fraction, the value otherwise. */
function cell(value: unknown): string | number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isInteger(value) ? value : +value.toFixed(1);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (Array.isArray(value)) return value.join('; ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Build the drawer payload for a slice of a dataset.
 *
 * Every row goes on the payload: the drawer renders the first 400 with the
 * demo's "Showing 400 of N" note, and its "Filter rows…" box searches the rest.
 *
 * Pure — no React, no fetching. Exported so a transform can prepare a payload
 * and the scene only has to open it.
 */
export function buildSlice(
  key: SliceKey,
  rows: readonly SliceRow[],
  options: SliceOptions = {},
): SourceTablePayload {
  const spec = SLICES[key];
  const cols = options.cols ?? spec.cols;
  const total = options.total ?? rows.length;
  const highlight = matchingIndexes(rows, options.highlight);
  return {
    kind: 'table',
    title: options.title ?? spec.title,
    columns: [...cols],
    rows: rows.map((row) => cols.map((c) => cell(at(row, c)))),
    provenance: [
      `meridian-data/${spec.path}`,
      spec.fmt.toUpperCase(),
      `${rows.length} of ${total} rows`,
      options.filter ? `filter: ${options.filter}` : null,
    ]
      .filter((part): part is string => part != null)
      .join(' · '),
    ...(highlight.length ? { highlight } : {}),
    ...(options.calc ? { calc: options.calc } : {}),
  };
}

/** Opens the `oem-source` drawer on rows the caller already holds. */
export type RowsDrawer = (
  key: SliceKey,
  rows: readonly SliceRow[],
  options?: SliceOptions,
) => void;

/**
 * Returns an opener. Synchronous: the rows are in hand, so there is no pending
 * state and no failure path.
 */
export function useRowsDrawer(): RowsDrawer {
  const { open } = useDrawer();
  return useCallback(
    (key, rows, options = {}) => {
      const payload = buildSlice(key, rows, options);
      open('oem-source', {
        wide: true,
        title: payload.title,
        data: payload as unknown as Record<string, unknown>,
      });
    },
    [open],
  );
}
